<template>
  <div v-if="modelValue?.length && ratios?.length" class="ratios-root">
    <div class="core-split-panes ratios-shell" :style="{ height: `${splitPanesHeightPx}px` }">
      <RatiosPaneItem
        v-for="(usageItem, index) in modelValue"
        :key="`${usageItem.type}-${usageItem.typeCounter}-${index}`"
        :topPx="paneLayouts[index]?.topPx ?? 0"
        :heightPx="paneLayouts[index]?.heightPx ?? MIN_VISIBLE_PANE_HEIGHT_PX"
        :fillFraction="paneLayouts[index]?.fillFraction ?? 1"
        :showRemove="allowRemoval && modelValue.length > 1"
        :usageItem="usageItem"
        :usageItems="modelValue"
        :usageIndex="index"
        :ratio="ratios[index]"
        :quantityArea="props.quantityArea ?? undefined"
        :onCommitRatio="(value) => onRatioInputCommit(index, value)"
        :onCommitArea="(value) => onAreaInputCommit(index, value)"
        :minRatio="minRatio"
        :minArea="minArea"
        :showAreaInput="showAreaInput"
        :titleResolver="titleResolver"
        @remove="onRemoveUsageItem(index)" />

      <RatiosDragHandle
        v-for="(handleTopPx, index) in handlePositionsPx"
        :key="`ratio-handle-${index}`"
        :topPx="Math.round(handleTopPx)"
        :active="dragSession?.handleIndex === index"
        @pointerdown="onHandlePointerDown(index, $event)"
        @nudge-up="onHandleNudge(index, 'up')"
        @nudge-down="onHandleNudge(index, 'down')" />
    </div>
  </div>
  <div v-else class="core-split-panes border p-2 text-neutral-550">
    {{ $t('project.general.sections.usage_definition.usage.hint.init') }}
  </div>
</template>

<script lang="ts" setup>
import { computed, nextTick, onUnmounted, ref, watch, type PropType } from 'vue'
import RatiosPaneItem from '@/components/partials/inputs/RatiosPaneItem.vue'
import RatiosDragHandle from '@/components/partials/inputs/RatiosDragHandle.vue'
import { deepClone } from '@/helpers/objects_arrays'
import type { UsageItem, SharedUsageItem } from '@/schemas/general/types'
import type { UsageType } from '@/schemas/general/enums'

type RatioEntry = UsageItem | SharedUsageItem

type DragSession = {
  handleIndex: number
  pointerId: number
  lastClientY: number
}

const props = withDefaults(
  defineProps<{
    allowRemoval?: boolean
    quantityArea?: number | null
    minRatio?: number
    minArea?: number
    showAreaInput?: boolean
    titleResolver?: (usageItem: RatioEntry, usageIndex: number) => string
  }>(),
  {
    allowRemoval: true,
    quantityArea: null,
    minRatio: 1,
    minArea: 1,
    showAreaInput: true,
    titleResolver: undefined,
  }
)
const allowRemoval = props.allowRemoval
const minRatio = props.minRatio
const minArea = props.minArea
const showAreaInput = props.showAreaInput
const titleResolver = props.titleResolver

const modelValue = defineModel({ type: Array as PropType<RatioEntry[]> })

const RATIO_PRECISION = 6
const BASE_SPLITPANES_HEIGHT_PX = 420
const MIN_VISIBLE_PANE_HEIGHT_PX = 60

const ratios = ref<number[] | null>(null)
const dragSession = ref<DragSession | null>(null)

const paneLayouts = computed(() => {
  const currentRatios = ratios.value ?? []
  let topPx = 0
  return currentRatios.map((ratio) => {
    const logicalPx = ratioToBasePixels(ratio)
    const heightPx = logicalToVisualPixels(logicalPx)
    const fillFraction = heightPx > 0 ? Math.min(1, Math.max(0, logicalPx / heightPx)) : 0
    const layout = { topPx, heightPx, logicalPx, fillFraction }
    topPx += heightPx
    return layout
  })
})

const splitPanesHeightPx = computed(() => {
  if (!paneLayouts.value.length) return BASE_SPLITPANES_HEIGHT_PX
  const last = paneLayouts.value[paneLayouts.value.length - 1]
  return last.topPx + last.heightPx
})

const handlePositionsPx = computed(() => {
  if (!paneLayouts.value.length) return []
  return paneLayouts.value.slice(0, -1).map((pane) => pane.topPx + pane.heightPx)
})

watch(
  modelValue,
  () => {
    if (dragSession.value) return
    ratios.value = modelValue.value?.map((usageItem) => usageItem.ratio) || null
  },
  { deep: true, immediate: true }
)

onUnmounted(() => {
  teardownDragListeners()
})

function onHandlePointerDown(handleIndex: number, event: PointerEvent): void {
  if (!ratios.value || !modelValue.value || handleIndex < 0 || handleIndex >= modelValue.value.length - 1) return

  dragSession.value = {
    handleIndex,
    pointerId: event.pointerId,
    lastClientY: event.clientY,
  }

  const target = event.currentTarget as HTMLElement | null
  target?.setPointerCapture?.(event.pointerId)

  window.addEventListener('pointermove', onWindowPointerMove)
  window.addEventListener('pointerup', onWindowPointerUp)
  window.addEventListener('pointercancel', onWindowPointerUp)
  document.body.style.userSelect = 'none'
}

function onWindowPointerMove(event: PointerEvent): void {
  const session = dragSession.value
  if (!session || event.pointerId !== session.pointerId) return
  if (!ratios.value) return

  const deltaVisualPx = event.clientY - session.lastClientY
  session.lastClientY = event.clientY
  if (Math.abs(deltaVisualPx) < 0.001) return

  applyHandleDragDelta(session.handleIndex, deltaVisualPx)
}

function onWindowPointerUp(event: PointerEvent): void {
  const session = dragSession.value
  if (!session || event.pointerId !== session.pointerId) return

  finalizeDrag(session.handleIndex)
  dragSession.value = null
  teardownDragListeners()
}

function teardownDragListeners(): void {
  window.removeEventListener('pointermove', onWindowPointerMove)
  window.removeEventListener('pointerup', onWindowPointerUp)
  window.removeEventListener('pointercancel', onWindowPointerUp)
  document.body.style.userSelect = ''
}

function applyHandleDragDelta(handleIndex: number, deltaVisualPx: number): void {
  const currentRatios = ratios.value
  if (!currentRatios || handleIndex < 0 || handleIndex >= currentRatios.length - 1) return

  const next = [...currentRatios]
  const topIndex = handleIndex
  const bottomIndex = handleIndex + 1

  const topLogicalPx = ratioToBasePixels(next[topIndex])
  const bottomLogicalPx = ratioToBasePixels(next[bottomIndex])
  const pairLogicalPx = topLogicalPx + bottomLogicalPx

  const topVisualPx = logicalToVisualPixels(topLogicalPx)
  const bottomVisualPx = logicalToVisualPixels(bottomLogicalPx)
  const pairVisualPx = topVisualPx + bottomVisualPx

  // When panes are visually inflated (deficit mode), slow the logical movement for better control.
  const visualToLogicalScale = pairVisualPx > 0 ? Math.min(1, pairLogicalPx / pairVisualPx) : 1
  const deltaLogicalPx = deltaVisualPx * visualToLogicalScale

  const minLogicalPx = ratioToBasePixels(getDragMinimumRatio())
  const nextTopLogicalPx = clampNumber(topLogicalPx + deltaLogicalPx, minLogicalPx, pairLogicalPx - minLogicalPx)
  const nextBottomLogicalPx = pairLogicalPx - nextTopLogicalPx

  next[topIndex] = basePixelsToRatio(nextTopLogicalPx)
  next[bottomIndex] = basePixelsToRatio(nextBottomLogicalPx)

  ratios.value = normalizeDraggedPairToTotal(next, topIndex, bottomIndex)
}

function finalizeDrag(handleIndex: number): void {
  if (!ratios.value) return
  const snapped = snapDraggedPairToWholePercent(ratios.value, handleIndex)
  ratios.value = snapped
  commitRatiosToModel(snapped)
}

function onHandleNudge(handleIndex: number, direction: 'up' | 'down'): void {
  const currentRatios = ratios.value
  if (!currentRatios || handleIndex < 0 || handleIndex >= currentRatios.length - 1) return

  const topIndex = handleIndex
  const bottomIndex = handleIndex + 1
  const minAllowed = getInputMinimumRatio()
  const step = 1

  const next = currentRatios.map((ratio) => roundToRatioPrecision(ratio))
  const pairTarget = roundToRatioPrecision(next[topIndex] + next[bottomIndex])
  const topDelta = direction === 'up' ? -step : step
  const nextTop = clampNumber(next[topIndex] + topDelta, minAllowed, pairTarget - minAllowed)
  if (isApproximatelyEqual(nextTop, next[topIndex])) return

  next[topIndex] = roundToRatioPrecision(nextTop)
  next[bottomIndex] = roundToRatioPrecision(pairTarget - next[topIndex])

  const normalized = normalizeDraggedPairToTotal(next, topIndex, bottomIndex)
  ratios.value = normalized
  commitRatiosToModel(normalized)
}

function commitRatiosToModel(nextRatios: number[]): void {
  if (!modelValue.value) return
  const copiedUsageItems = deepClone(modelValue.value)
  nextRatios.forEach((ratio, index) => {
    if (copiedUsageItems[index]) copiedUsageItems[index].ratio = ratio
  })
  modelValue.value = copiedUsageItems
}

function ratioToBasePixels(ratio: number): number {
  return (ratio / 100) * BASE_SPLITPANES_HEIGHT_PX
}

function basePixelsToRatio(px: number): number {
  return (px / BASE_SPLITPANES_HEIGHT_PX) * 100
}

function logicalToVisualPixels(logicalPx: number): number {
  return Math.max(logicalPx, MIN_VISIBLE_PANE_HEIGHT_PX)
}

function getDragMinimumRatio(): number {
  const quantityArea = Number(props.quantityArea ?? 0)
  const oneSquareMeterRatio = quantityArea > 0 ? 100 / quantityArea : 1
  return Math.min(minRatio, oneSquareMeterRatio)
}

function getInputMinimumRatio(): number {
  return getDragMinimumRatio()
}

function clampNumber(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max)
}

function normalizeRatios(values: number[]): number[] {
  if (!values.length) return []

  const precisionFactor = 10 ** RATIO_PRECISION
  const sanitized = values.map((value) => Math.max(0, Number.isFinite(value) ? value : 0))
  const scaled = sanitized.map((value) => Math.round(value * precisionFactor))
  let sum = scaled.reduce((acc, value) => acc + value, 0)

  if (sum === 0) {
    const equalShare = Math.round((100 / scaled.length) * precisionFactor)
    const result = scaled.map(() => equalShare)
    const diff = 100 * precisionFactor - result.reduce((acc, value) => acc + value, 0)
    result[0] += diff
    return result.map((value) => value / precisionFactor)
  }

  let largestIndex = 0
  scaled.forEach((value, index) => {
    if (value > scaled[largestIndex]) largestIndex = index
  })

  const diff = 100 * precisionFactor - sum
  scaled[largestIndex] += diff
  sum += diff
  if (sum !== 100 * precisionFactor) {
    scaled[largestIndex] += 100 * precisionFactor - sum
  }

  return scaled.map((value) => value / precisionFactor)
}

function normalizeDraggedPairToTotal(values: number[], topIndex: number, bottomIndex: number): number[] {
  const next = values.map((value) => roundToRatioPrecision(value))
  const othersSum = next.reduce((sum, value, index) => {
    if (index === topIndex || index === bottomIndex) return sum
    return sum + value
  }, 0)

  const pairTarget = roundToRatioPrecision(100 - othersSum)
  const pairSum = roundToRatioPrecision(next[topIndex] + next[bottomIndex])
  if (!isApproximatelyEqual(pairSum, pairTarget)) {
    next[bottomIndex] = roundToRatioPrecision(next[bottomIndex] + (pairTarget - pairSum))
  }

  const finalSum = next.reduce((sum, value) => sum + value, 0)
  if (!isApproximatelyEqual(finalSum, 100)) {
    next[bottomIndex] += 100 - finalSum
  }

  return next
}

function snapDraggedPairToWholePercent(values: number[], splitterIndex: number): number[] {
  const topIndex = splitterIndex
  const bottomIndex = splitterIndex + 1
  if (bottomIndex >= values.length) return values

  const next = values.map((value) => roundToRatioPrecision(value))
  const othersSum = next.reduce((sum, value, index) => {
    if (index === topIndex || index === bottomIndex) return sum
    return sum + value
  }, 0)
  const pairTarget = 100 - othersSum

  const dragMin = getDragMinimumRatio()
  const snappedTop = clampNumber(Math.round(next[topIndex]), dragMin, pairTarget - dragMin)
  next[topIndex] = roundToRatioPrecision(snappedTop)
  next[bottomIndex] = roundToRatioPrecision(pairTarget - next[topIndex])

  return normalizeDraggedPairToTotal(next, topIndex, bottomIndex)
}

async function preserveAreaDistributionScrollPosition(previousTop: number | null): Promise<void> {
  if (previousTop === null) return
  await nextTick()
  await new Promise<void>((resolve) => window.requestAnimationFrame(() => resolve()))
  const nextTop = getAreaDistributionTop()
  if (nextTop === null) return
  window.scrollBy({ top: nextTop - previousTop, behavior: 'auto' })
}

function getAreaDistributionTop(): number | null {
  const section = document.getElementById('area_distribution')
  if (!section) return null
  return section.getBoundingClientRect().top
}

async function onRemoveUsageItem(index: number): Promise<void> {
  if (!modelValue.value) return
  const previousAreaDistributionTop = getAreaDistributionTop()
  const copiedUsageItems = deepClone(modelValue.value)
  copiedUsageItems.splice(index, 1)
  updateTypeCounter(copiedUsageItems)

  if (!copiedUsageItems.length) {
    modelValue.value = copiedUsageItems
    ratios.value = []
    await preserveAreaDistributionScrollPosition(previousAreaDistributionTop)
    return
  }

  const normalized = normalizeRatios(copiedUsageItems.map((usage) => usage.ratio))
  normalized.forEach((ratio, itemIndex) => {
    copiedUsageItems[itemIndex].ratio = ratio
  })

  ratios.value = normalized
  modelValue.value = copiedUsageItems
  await preserveAreaDistributionScrollPosition(previousAreaDistributionTop)
}

function onRatioInputCommit(index: number, requestedRatio: number): boolean {
  if (!modelValue.value || !ratios.value) return false
  if (!Number.isFinite(requestedRatio) || requestedRatio < minRatio || requestedRatio > 100) return false

  const copiedUsageItems = deepClone(modelValue.value)
  const usageCount = copiedUsageItems.length
  if (usageCount === 0) return false

  if (usageCount === 1) {
    if (!isApproximatelyEqual(requestedRatio, 100)) return false
    return true
  }

  const minAllowed = getInputMinimumRatio()
  const maxAllowed = 100 - minAllowed * (usageCount - 1)
  const nextRatio = roundToRatioPrecision(requestedRatio)
  if (requestedRatio < minAllowed || requestedRatio > maxAllowed) return false

  const currentRatio = copiedUsageItems[index].ratio
  const delta = roundToRatioPrecision(nextRatio - currentRatio)
  const preferredIndices = getPreferredCompensationOrder(index, usageCount)
  const rebalanced = applyDeltaWithPreferredOrder(
    copiedUsageItems.map((item) => item.ratio),
    index,
    delta,
    preferredIndices,
    minAllowed
  )
  if (!rebalanced) return false

  const normalized = normalizeValuesToTotal(rebalanced, 100)
  normalized.forEach((ratio, itemIndex) => {
    copiedUsageItems[itemIndex].ratio = ratio
  })

  ratios.value = normalized
  modelValue.value = copiedUsageItems
  return true
}

function onAreaInputCommit(index: number, requestedArea: number): boolean {
  const quantityArea = Number(props.quantityArea ?? 0)
  if (!Number.isFinite(quantityArea) || quantityArea <= 0) return false
  if (!Number.isFinite(requestedArea) || requestedArea < minArea || requestedArea > quantityArea) return false

  if ((modelValue.value?.length ?? 0) === 1) {
    return isApproximatelyEqual(requestedArea, quantityArea)
  }

  const requestedRatio = (requestedArea / quantityArea) * 100
  return onRatioInputCommit(index, requestedRatio)
}

function getPreferredCompensationOrder(index: number, itemCount: number): number[] {
  const below: number[] = []
  for (let i = index + 1; i < itemCount; i += 1) below.push(i)
  const above: number[] = []
  for (let i = index - 1; i >= 0; i -= 1) above.push(i)
  return [...below, ...above]
}

function applyDeltaWithPreferredOrder(
  ratiosInput: number[],
  targetIndex: number,
  delta: number,
  preferredIndices: number[],
  minPerItem: number
): number[] | null {
  const next = ratiosInput.map((value) => roundToRatioPrecision(value))
  next[targetIndex] = roundToRatioPrecision(next[targetIndex] + delta)

  if (delta > 0) {
    let remaining = delta
    for (const idx of preferredIndices) {
      if (remaining <= 1e-9) break
      const available = Math.max(0, next[idx] - minPerItem)
      if (available <= 0) continue
      const take = Math.min(available, remaining)
      next[idx] = roundToRatioPrecision(next[idx] - take)
      remaining = roundToRatioPrecision(remaining - take)
    }
    if (remaining > 1e-6) return null
  } else if (delta < 0) {
    let remaining = -delta
    for (const idx of preferredIndices) {
      if (remaining <= 1e-9) break
      next[idx] = roundToRatioPrecision(next[idx] + remaining)
      remaining = 0
    }
    if (remaining > 1e-6) return null
  }

  if (next.some((value, idx) => idx !== targetIndex && value + 1e-9 < minPerItem)) return null
  if (next[targetIndex] + 1e-9 < minPerItem) return null

  return normalizeValuesToTotal(next, 100)
}

function normalizeValuesToTotal(values: number[], targetTotal: number): number[] {
  if (!values.length) return []

  const rounded = values.map((value) => roundToRatioPrecision(value))
  let sum = rounded.reduce((acc, value) => acc + value, 0)
  let largestIndex = 0
  rounded.forEach((value, index) => {
    if (value > rounded[largestIndex]) largestIndex = index
  })

  if (!isApproximatelyEqual(sum, targetTotal)) {
    rounded[largestIndex] = roundToRatioPrecision(rounded[largestIndex] + (targetTotal - sum))
    sum = rounded.reduce((acc, value) => acc + value, 0)
  }
  if (!isApproximatelyEqual(sum, targetTotal)) {
    rounded[largestIndex] += targetTotal - sum
  }

  return rounded
}

function roundToRatioPrecision(value: number): number {
  const factor = 10 ** RATIO_PRECISION
  return Math.round(value * factor) / factor
}

function isApproximatelyEqual(a: number, b: number, epsilon = 1e-6): boolean {
  return Math.abs(a - b) <= epsilon
}

function updateTypeCounter(usageItems: RatioEntry[]): void {
  const typeCount = new Map<UsageType, number>()
  usageItems.forEach((usage) => {
    usage.typeCounter = typeCount.get(usage.type) || 0
    typeCount.set(usage.type, usage.typeCounter + 1)
  })
}
</script>

<style lang="postcss" scoped>
.ratios-root {
  @apply relative;
}

.core-split-panes {
  @apply relative w-full overflow-visible;
}

.ratios-shell {
  border-radius: 0;
}

.ratios-shell::after {
  content: '';
  position: absolute;
  inset: 0;
  border: 1px solid rgba(209, 213, 219, 1);
  pointer-events: none;
  z-index: 0;
}
</style>
